import { useState, useEffect, useCallback } from "react";

import api from "@services/api";
import { useToast } from "@hooks/useToast";
import useTranslation from "src/translations/useTranslation";
import { ToastMessages } from "@shared/languages/interfaces";

import { TravelAnalysisMapMarker, TravelAnalysisFilter } from "../types";

const USE_MOCK_DATA = false; // TODO: revert to `process.env.NODE_ENV === "development"` when needed

/** Mock markers grouped by origin for visual testing during development. */
const MOCK_MARKERS: TravelAnalysisMapMarker[] = [
  {
    latitude: "-23.5505",
    longitude: "-46.6333",
    originName: "CD São Paulo - SP",
    totalAlerts: 18,
    travelCount: 12,
    alertsByType: [
      { type: "Instabilidade de conexão GPRS", count: 7 },
      { type: "Déficit de comandos de sensor ativo (IDLE elevado)", count: 5 },
      { type: "Informações de CAN travadas (completas ou parciais)", count: 4 },
      { type: "Falha de posicionamento GPS", count: 2 }
    ]
  },
  {
    latitude: "-22.9068",
    longitude: "-43.1729",
    originName: "CD Rio de Janeiro - RJ",
    totalAlerts: 9,
    travelCount: 6,
    alertsByType: [
      { type: "Informações de CAN travadas (completas ou parciais)", count: 4 },
      { type: "Inconsistência de fluxo logístico", count: 3 },
      { type: "Ciclo transversal (D+1)", count: 2 }
    ]
  },
  {
    latitude: "-25.4284",
    longitude: "-49.2733",
    originName: "CD Curitiba - PR",
    totalAlerts: 5,
    travelCount: 4,
    alertsByType: [
      { type: "Falha de posicionamento GPS", count: 3 },
      { type: "Déficit de comandos de hardware enviados", count: 2 }
    ]
  },
  {
    latitude: "-23.1896",
    longitude: "-46.8844",
    originName: "CD Jundiaí - SP",
    totalAlerts: 3,
    travelCount: 2,
    alertsByType: [
      { type: "Instabilidade de conexão GPRS", count: 2 },
      { type: "Déficit de comandos de sensor ativo (IDLE elevado)", count: 1 }
    ]
  },
  {
    latitude: "-19.9167",
    longitude: "-43.9345",
    originName: "CD Belo Horizonte - MG",
    totalAlerts: 11,
    travelCount: 7,
    alertsByType: [
      { type: "Inconsistência de fluxo logístico", count: 5 },
      { type: "Ciclo transversal (D+1)", count: 3 },
      { type: "Instabilidade de conexão GPRS", count: 2 },
      { type: "Falha de posicionamento GPS", count: 1 }
    ]
  }
];

/** Return shape of the `useTravelAnalysisMapMarkers` hook. */
interface UseTravelAnalysisMapMarkersReturn {
  markers: TravelAnalysisMapMarker[];
  markersLoading: boolean;
}

/**
 * Fetches lightweight map markers for the geographic monitoring section.
 * Automatically refetches when the dashboard filter changes.
 */
const useTravelAnalysisMapMarkers = (currentFilter: TravelAnalysisFilter | null): UseTravelAnalysisMapMarkersReturn => {

  const { addToast } = useToast();
  const { t } = useTranslation();

  const [markers, setMarkers] = useState<TravelAnalysisMapMarker[]>([]);
  const [markersLoading, setMarkersLoading] = useState(false);

  const fetchMarkers = useCallback(async (filter: TravelAnalysisFilter) => {

    if (USE_MOCK_DATA) {
      setMarkersLoading(true);
      setTimeout(() => {
        setMarkers(MOCK_MARKERS);
        setMarkersLoading(false);
      }, 600);

      return;
    }

    setMarkersLoading(true);

    try {

      const { data } = await api.post("travels/analysis/map-markers", filter);

      if (data.status === "success") {
        setMarkers(data.result || []);
      }

    } catch (err) {
      const message = err?.response?.data?.message || t(ToastMessages.connectionNotEstablished);

      addToast({ type: "error", title: t(ToastMessages.error), description: message });
    } finally {
      setMarkersLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when the dashboard filter changes
  useEffect(() => {
    if (currentFilter) {
      fetchMarkers(currentFilter);
    }
  }, [currentFilter, fetchMarkers]);

  return { markers, markersLoading };
};

export default useTravelAnalysisMapMarkers;
